import {addDays, differenceInCalendarDays, format} from 'date-fns'

export interface QuoteMoney {
  readonly amount: number
  readonly currency: string
}

export interface ModifyQuoteResponse {
  readonly eligible: boolean
  readonly reason?: string
  readonly message?: string
  readonly kind?: string
  readonly pricingPath?: string
  readonly currentTotal?: QuoteMoney
  readonly newTotal?: QuoteMoney
  readonly delta?: QuoteMoney
  /** Taxes and fees Apaleo reports on top of the quoted room totals */
  readonly taxes?: QuoteMoney
  readonly fees?: QuoteMoney
  /** Room total + taxes + fees for the newly priced portion */
  readonly grandTotal?: QuoteMoney
  /** The current stay's charges and full total (estimated by the backend) */
  readonly currentTaxes?: QuoteMoney
  readonly currentFees?: QuoteMoney
  readonly currentGrandTotal?: QuoteMoney
  readonly detail?: Record<string, unknown>
}

/** The room-level total the guest is confirming */
export const confirmationTotal = (
  quote: ModifyQuoteResponse
): QuoteMoney | undefined => quote.newTotal

export const formatMoney = (money?: QuoteMoney): string => {
  if (!money) {
    return '—'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency
  }).format(money.amount)
}

/** yyyy-MM-dd for the API from a locally-picked Date (timezone-agnostic) */
export const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd')

/**
 * Local-midnight Date from the calendar-date part of an ISO string.
 * Ignores time and offset so the stay's date never shifts in browsers
 * whose timezone differs from the property's
 */
export const toLocalDate = (isoDate?: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '')
  if (!match) {
    return undefined
  }
  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/** Guest-facing copy for each backend ineligibility reason */
export const ineligibleMessageFor = (reason?: string): string => {
  switch (reason) {
    case 'monthly-excluded':
    case 'monthly-target-excluded':
      return 'Changes to monthly stays are handled by our team — reach out and we’ll take care of it.'
    case 'prepaid-change-blocked':
      return 'Prepaid stays can’t be changed online. Contact the property and we’ll take care of it.'
    case 'channel-not-allowed':
      return 'This reservation was booked through a partner site, so date changes happen there.'
    case 'corporate-excluded':
      return 'Corporate reservations are handled by our team — reach out and we’ll take care of it.'
    case 'capture-window-locked':
      return (
        'Your stay is coming up soon, so online date changes are closed. ' +
        'Contact the property and we’ll take care of it.'
      )
    case 'in-house-arrival-shift-blocked':
      return 'Your stay has already started, so check-in can’t change — but check-out can.'
    case 'in-house-tier-switch-blocked':
      return 'That change would switch your rate type mid-stay. Contact the property and we’ll help from there.'
    case 'status-not-modifiable':
      return 'This reservation can’t be changed anymore.'
    case 'no-change':
      return 'Those are your current dates — pick a new check-in or check-out.'
    case 'invalid-dates':
      return 'Those dates don’t look right — check-out needs to come after check-in, and dates can’t be in the past.'
    default:
      return 'This change isn’t available online. Contact the property and we’ll help from there.'
  }
}

/** Guest-facing copy for apply-time API errors, keyed by response */
export const applyErrorMessageFor = (error: {
  status?: number
  data?: {error?: string}
}): string => {
  if (error?.data?.error === 'quote-changed') {
    return (
      'Pricing changed while you were confirming. Here’s the updated ' +
      'total — confirm again if it still works for you.'
    )
  }
  if (error?.data?.error === 'modify-in-progress') {
    return 'A change to this reservation is already being processed. Give it a moment and try again.'
  }
  if (error?.status === 403) {
    return 'Online date changes aren’t available for this property yet. Contact the property and we’ll take care of it.'
  }
  return 'We couldn’t update your reservation. Try again or contact the property.'
}

/** e.g. "Sun, Aug 30" */
const formatRecapDate = (date: Date): string => format(date, 'EEE, MMM d')

const nightsLabel = (count: number): string =>
  `${count} ${count === 1 ? 'night' : 'nights'}`

/** Signed money for the difference line, e.g. "+$100.00" / "−$100.00" */
const signedMoney = (money: QuoteMoney): string => {
  const sign = money.amount > 0 ? '+' : money.amount < 0 ? '−' : ''
  return `${sign}${formatMoney({
    amount: Math.abs(money.amount),
    currency: money.currency
  })}`
}

export interface RecapRow {
  readonly label: string
  readonly previous: string
  readonly next: string
  readonly changed: boolean
}

/**
 * Old → new comparison rows for the recap: check-in, check-out, stay
 * length. Unchanged rows are flagged so the UI can say "unchanged"
 * instead of drawing a misleading arrow
 */
export const recapRows = (
  currentCheckin: string | undefined,
  currentCheckout: string | undefined,
  newArrival: Date,
  newDeparture: Date
): readonly RecapRow[] => {
  const previousArrival = toLocalDate(currentCheckin)
  const previousDeparture = toLocalDate(currentCheckout)

  const dateRow = (label: string, next: Date, previous?: Date): RecapRow => ({
    label,
    previous: previous ? formatRecapDate(previous) : '—',
    next: formatRecapDate(next),
    changed: !previous || toIsoDate(previous) !== toIsoDate(next)
  })

  const previousNights =
    previousArrival && previousDeparture
      ? differenceInCalendarDays(previousDeparture, previousArrival)
      : undefined
  const newNights = differenceInCalendarDays(newDeparture, newArrival)

  return [
    dateRow('Check-in', newArrival, previousArrival),
    dateRow('Check-out', newDeparture, previousDeparture),
    {
      label: 'Stay length',
      previous:
        previousNights === undefined ? '—' : nightsLabel(previousNights),
      next: nightsLabel(newNights),
      changed: previousNights !== newNights
    }
  ]
}

/** Nights of the new stay that aren't part of the current stay */
const addedNightDates = (
  previousArrival: Date,
  previousDeparture: Date,
  newArrival: Date,
  newDeparture: Date
): readonly Date[] => {
  const newNights = differenceInCalendarDays(newDeparture, newArrival)
  return Array.from({length: Math.max(newNights, 0)}, (_, i) =>
    addDays(newArrival, i)
  ).filter(night => night < previousArrival || night >= previousDeparture)
}

/** "Added night · Sep 2" / "Added nights · Sep 2 – Sep 4" */
const addedNightsLabel = (nights: readonly Date[]): string => {
  const short = (date: Date) => format(date, 'MMM d')
  if (nights.length === 1) {
    return `Added night · ${short(nights[0])}`
  }
  return `Added nights · ${short(nights[0])} – ${short(
    nights[nights.length - 1]
  )}`
}

export interface RecapPriceLine {
  readonly label: string
  readonly value: string
  readonly kind: 'line' | 'total' | 'increase' | 'decrease'
}

const chargesScopeOf = (quote: ModifyQuoteResponse): string =>
  String(quote.detail?.taxesAndFeesScope ?? '')

const round2 = (amount: number): number => Math.round(amount * 100) / 100

const moneyLine = (
  label: string,
  money: QuoteMoney | undefined,
  kind: RecapPriceLine['kind'] = 'line'
): readonly RecapPriceLine[] =>
  money ? [{label, value: formatMoney(money), kind}] : []

export interface RecapPriceGroup {
  readonly title: string
  readonly lines: readonly RecapPriceLine[]
}

export interface RecapMoneyBlock {
  readonly groups: readonly RecapPriceGroup[]
  /** Emphasized closing row: the new grand total */
  readonly footer: readonly RecapPriceLine[]
}

/**
 * The current reservation's section: a single total line when the backend
 * couldn't estimate its charges, or room / taxes / fees / total when it
 * could
 */
const currentStayGroup = (quote: ModifyQuoteResponse): RecapPriceGroup => {
  const title = 'Current stay'
  if (!quote.currentGrandTotal) {
    return {title, lines: moneyLine('Total', quote.currentTotal, 'total')}
  }
  return {
    title,
    lines: [
      ...moneyLine('Room', quote.currentTotal),
      ...moneyLine('Taxes', quote.currentTaxes),
      ...moneyLine('Fees', quote.currentFees),
      ...moneyLine('Total', quote.currentGrandTotal, 'total')
    ]
  }
}

/** next − previous, treating a missing side as zero */
const diffMoney = (
  next?: QuoteMoney,
  previous?: QuoteMoney
): QuoteMoney | undefined => {
  const currency = next?.currency ?? previous?.currency
  if (!currency) {
    return undefined
  }
  return {
    amount: round2((next?.amount ?? 0) - (previous?.amount ?? 0)),
    currency
  }
}

/** A signed +/− line, omitted when the change is zero or unknown */
const signedLine = (
  label: string,
  money?: QuoteMoney
): readonly RecapPriceLine[] =>
  money && money.amount !== 0
    ? [{label, value: signedMoney(money), kind: 'line'}]
    : []

interface RecapChanges {
  readonly group: RecapPriceGroup
  /** Sum of the additional amounts: what the whole change costs (signed) */
  readonly total?: QuoteMoney
}

/**
 * The additional amounts the change adds (or removes): room, taxes, fees
 * as signed deltas with a signed, colored subtotal. Taxes/fees deltas are
 * the added window's charges directly when that's their scope, otherwise
 * new-stay charges minus the current stay's — omitted when the current
 * side is unknown, so an "additional" amount is never overstated.
 */
const changesGroup = (
  quote: ModifyQuoteResponse,
  title: string
): RecapChanges => {
  const scope = chargesScopeOf(quote)
  const chargeDelta = (
    next?: QuoteMoney,
    current?: QuoteMoney
  ): QuoteMoney | undefined => {
    if (scope === 'added-nights') {
      return next
    }
    return quote.currentGrandTotal ? diffMoney(next, current) : undefined
  }
  const roomDelta = quote.delta
  const taxesDelta = chargeDelta(quote.taxes, quote.currentTaxes)
  const feesDelta = chargeDelta(quote.fees, quote.currentFees)

  const parts = [roomDelta, taxesDelta, feesDelta].filter(Boolean)
  const total =
    roomDelta && parts.length > 0
      ? {
          amount: round2(parts.reduce((sum, part) => sum + part.amount, 0)),
          currency: roomDelta.currency
        }
      : undefined
  const itemLines = [
    ...signedLine('Room', roomDelta),
    ...signedLine('Taxes', taxesDelta),
    ...signedLine('Fees', feesDelta)
  ]
  return {group: {title, lines: itemLines}, total}
}

/**
 * The recap money block: the current stay's amounts, the additional
 * amounts the change costs, and a footer carrying only the new grand
 * total
 */
export const recapMoneyBlock = (
  quote: ModifyQuoteResponse,
  currentCheckin: string | undefined,
  currentCheckout: string | undefined,
  newArrival: Date,
  newDeparture: Date
): RecapMoneyBlock => {
  const previousArrival = toLocalDate(currentCheckin)
  const previousDeparture = toLocalDate(currentCheckout)
  const deltaAmount = quote.delta?.amount ?? 0

  const addedNights =
    quote.kind === 'extension' &&
    deltaAmount > 0 &&
    previousArrival &&
    previousDeparture
      ? addedNightDates(
          previousArrival,
          previousDeparture,
          newArrival,
          newDeparture
        )
      : []
  const changesTitle =
    addedNights.length > 0 ? addedNightsLabel(addedNights) : 'Your changes'

  const changes = changesGroup(quote, changesTitle)

  // The full new cost: current full total + the change, or the backend's
  // grand total when it already covers the whole new stay
  const grandNewTotal =
    chargesScopeOf(quote) === 'new-stay' && quote.grandTotal
      ? quote.grandTotal
      : quote.currentGrandTotal && changes.total
      ? {
          amount: round2(quote.currentGrandTotal.amount + changes.total.amount),
          currency: quote.currentGrandTotal.currency
        }
      : quote.grandTotal ?? quote.newTotal

  // What the whole change costs, shown beside the grand total: the summed
  // additional amounts when itemized, else grand-vs-current as a fallback
  const difference =
    changes.total ??
    diffMoney(grandNewTotal, quote.currentGrandTotal ?? quote.currentTotal)
  const differenceLine: readonly RecapPriceLine[] =
    difference && difference.amount !== 0
      ? [
          {
            label: 'Difference',
            value: signedMoney(difference),
            kind: difference.amount > 0 ? 'increase' : 'decrease'
          }
        ]
      : []

  return {
    groups: [currentStayGroup(quote), changes.group].filter(
      group => group.lines.length > 0
    ),
    footer: [
      ...moneyLine('New total', grandNewTotal, 'total'),
      ...differenceLine
    ]
  }
}

/**
 * The single context note under the recap money block — one per quote,
 * picked in priority order so the recap stays scannable
 */
export const recapNote = (quote: ModifyQuoteResponse): string => {
  if (quote.detail?.sameDayNightCharged) {
    return 'Since it’s after 11 AM, tonight is still part of your stay.'
  }
  if (quote.kind === 'reduction') {
    return (
      'Your reservation total updates when you confirm. Any billing ' +
      'adjustment shows on your folio shortly after.'
    )
  }
  if (quote.grandTotal) {
    return chargesScopeOf(quote) === 'added-nights'
      ? 'Taxes and fees shown cover the added nights.'
      : 'Taxes and fees are included in the new total.'
  }
  return 'Taxes and fees aren’t included in the totals above.'
}
